import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import AuthContext from '../contexts/AuthContext';
import { getFileUrl } from '../utils/fileUtil';

// Which upstream boards a mirrored feed (Sprock) is allowed to land on the Command Center.
const MirrorSettings = ({ token, source = 'sprock' }) => {
  const [settings, setSettings] = useState(null);
  const [mode, setMode] = useState('all');
  const [boards, setBoards] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    axios.get('/api/cards/sync/external/settings', { params: { source }, headers: { 'x-auth-token': token } })
      .then(res => {
        if (!alive) return;
        setSettings(res.data);
        setMode(res.data.mode || 'all');
        setBoards(res.data.boards || []);
      })
      .catch(() => alive && setError('Could not load mirror settings'));
    return () => { alive = false; };
  }, [token, source]);

  if (!settings || (!settings.known.length && !settings.lastSyncAt)) return null; // feed never reported for this user

  const toggle = (name) => setBoards(prev => prev.includes(name) ? prev.filter(b => b !== name) : [...prev, name]);
  const dirty = mode !== settings.mode || boards.length !== settings.boards.length || boards.some(b => !settings.boards.includes(b));
  const onSave = async () => {
    setSaving(true); setError('');
    try {
      const res = await axios.put('/api/cards/sync/external/settings', { source, mode, boards }, { headers: { 'x-auth-token': token } });
      setSettings(res.data);
      setSavedAt(new Date());
    } catch (err) {
      setError('Could not save mirror settings');
    } finally {
      setSaving(false);
    }
  };
  const stale = (d) => d && (Date.now() - new Date(d).getTime()) > 2 * 24 * 3600 * 1000;

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden mt-8">
      <div className="p-6 sm:p-8 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900">Mirrored Boards</h2>
        <p className="text-gray-500 mt-1">
          {settings.label} cards assigned to you are mirrored onto your Command Center. Choose which hub boards are allowed through.
          Unchecking a board archives the cards already mirrored from it on the next sync; checking it again brings them back.
        </p>
        {settings.lastSyncAt && (
          <p className="text-xs text-gray-400 mt-2">Last sync {new Date(settings.lastSyncAt).toLocaleString()}</p>
        )}
      </div>
      <div className="p-6 sm:p-8 space-y-5">
        <div className="space-y-2">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input type="radio" name="mirror-mode" className="mt-1" checked={mode === 'all'} onChange={() => setMode('all')} />
            <span>
              <span className="font-bold text-gray-800">All boards</span>
              <span className="block text-sm text-gray-500">Everything assigned to you, including boards that appear later.</span>
            </span>
          </label>
          <label className="flex items-start space-x-3 cursor-pointer">
            <input type="radio" name="mirror-mode" className="mt-1" checked={mode === 'selected'} onChange={() => setMode('selected')} />
            <span>
              <span className="font-bold text-gray-800">Only the boards I pick</span>
              <span className="block text-sm text-gray-500">New hub boards stay off the Command Center until you check them here.</span>
            </span>
          </label>
        </div>

        <div className={`border border-gray-200 rounded-md divide-y divide-gray-100 ${mode === 'selected' ? '' : 'opacity-50'}`}>
          {settings.known.map(k => (
            <label key={k.name} className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50">
              <span className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary-blue focus:ring-primary-blue"
                  checked={mode === 'all' || boards.includes(k.name)}
                  disabled={mode !== 'selected'}
                  onChange={() => toggle(k.name)}
                />
                <span className="font-medium text-gray-800">{k.name}</span>
              </span>
              <span className={`text-xs ${stale(k.lastSeen) ? 'text-gray-400 italic' : 'text-gray-500'}`}>
                {stale(k.lastSeen) ? `not on the feed since ${new Date(k.lastSeen).toLocaleDateString()}` : `${k.count} open`}
              </span>
            </label>
          ))}
          {!settings.known.length && (
            <div className="px-4 py-3 text-sm text-gray-500">No boards reported yet. They appear here after the next sync.</div>
          )}
        </div>

        <div className="flex items-center space-x-4">
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !dirty}
            className="px-6 py-2 bg-primary-blue text-white font-bold rounded-md hover:bg-blue-700 transition-colors shadow-md disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Mirror Settings'}
          </button>
          {savedAt && !dirty && <span className="text-sm text-green-600">Saved. Takes effect on the next sync (within 15 minutes).</span>}
          {error && <span className="text-sm text-red-600">{error}</span>}
        </div>
      </div>
    </div>
  );
};

const Profile = () => {
  const { currentUser, token, loadUser } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setFormData({
        name: currentUser.name || ''
      });
    }
  }, [currentUser]);

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put('/api/users/me', formData, {
        headers: { 'x-auth-token': token }
      });
      await loadUser();
      alert('Profile updated successfully');
    } catch (err) {
      alert('Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadData = new FormData();
    uploadData.append('avatar', file);

    setUploading(true);
    try {
      const res = await axios.post('/api/users/me/avatar', uploadData, {
        headers: { 
          'x-auth-token': token,
          'Content-Type': 'multipart/form-data'
        }
      });
      console.log('Upload response:', res.data);
      await loadUser();
      alert('Avatar updated successfully!');
    } catch (err) {
      console.error('Full upload error:', err);
      alert(`Error uploading avatar: ${err.response?.data?.msg || err.message}`);
    } finally {
      setUploading(false);
    }
  };

  if (!currentUser) return null;

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Your Profile</h1>
      
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-6 sm:p-8 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="relative">
              <div className="h-32 w-32 rounded-full bg-secondary-cyan flex items-center justify-center text-white text-4xl font-bold overflow-hidden border-4 border-white shadow-lg">
                {currentUser.avatar ? (
                  <img 
                    src={getFileUrl(currentUser.avatar)} 
                    alt={currentUser.name} 
                    className="h-full w-full object-cover" 
                  />
                ) : (
                  currentUser.name.charAt(0).toUpperCase()
                )}
              </div>
              <label className="absolute bottom-0 right-0 bg-primary-blue text-white p-2 rounded-full cursor-pointer shadow-md hover:bg-blue-700 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                <input type="file" className="hidden" onChange={handleAvatarUpload} disabled={uploading} />
              </label>
            </div>
            
            <div className="text-center sm:text-left">
              <h2 className="text-xl font-bold text-gray-900">{currentUser.name}</h2>
              <p className="text-gray-500">{currentUser.email}</p>
              <div className="mt-2">
                <span className={`px-2 py-1 text-xs font-bold rounded-full uppercase ${currentUser.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                  {currentUser.role}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-6 sm:p-8">
          <form onSubmit={onSubmit} className="max-w-md space-y-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider mb-2">Display Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={onChange}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-blue focus:border-primary-blue p-2"
                required
              />
            </div>
            
            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-primary-blue text-white font-bold rounded-md hover:bg-blue-700 transition-colors shadow-md disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <MirrorSettings token={token} />
    </div>
  );
};

export default Profile;